import { cx } from '@emotion/css';
import {
  parseDocumentFragmentFromString,
  serializeDocumentFragmentIntoString
} from '@msinternal/botframework-webchat-component-better-link';
import { hooks, StrictStyleOptions } from 'botframework-webchat-api';
import { useMemo } from 'react';

import { useStyles } from '@msinternal/botframework-webchat-styles/react';
import { useTransformHTMLContent } from '../providers/HTMLContentTransformCOR/index';
import useWebChatUIContext from './internal/useWebChatUIContext';

import styles from './RenderMarkdown.module.css';

const { useLocalizer, useStyleOptions } = hooks;

type RenderMarkdownAsHTMLFn = (
  markdown: string,
  styleOptions?: Readonly<StrictStyleOptions>,
  options?: Readonly<{ externalLinkAlt: string }>
) => string;

export default function useRenderMarkdownAsHTML(
  mode: 'accessible name' | 'adaptive cards' | 'citation modal' | 'message activity' = 'message activity'
): RenderMarkdownAsHTMLFn | undefined {
  const { renderMarkdown } = useWebChatUIContext();
  const [styleOptions] = useStyleOptions();
  const localize = useLocalizer();
  const transformHTMLContent = useTransformHTMLContent();

  const classNames = useStyles(styles);

  const externalLinkAlt = localize('MARKDOWN_EXTERNAL_LINK_ALT');

  const containerClassName = useMemo(
    () =>
      cx(classNames['render-markdown'], {
        [classNames['render-markdown--adaptive-cards']]: mode === 'adaptive cards',
        [classNames['render-markdown--citation']]: mode === 'citation modal',
        [classNames['render-markdown--message-activity']]:
          mode !== 'accessible name' && mode !== 'adaptive cards' && mode !== 'citation modal'
      }),
    [classNames, mode]
  );

  // For 'message activity' mode, streaming rendering is available via useStreamingMarkdownWithDefinitions hook.
  // This hook still returns a full-render function for backward compatibility and other consumers.
  return useMemo(
    () =>
      renderMarkdown &&
      (markdown => {
        const html = renderMarkdown(markdown, styleOptions, { externalLinkAlt });

        const documentFragment = transformHTMLContent(parseDocumentFragmentFromString(html));

        const rootElement = document.createElement('div');

        containerClassName && rootElement.classList.add(...containerClassName.split(' ').filter(Boolean));

        rootElement.append(...documentFragment.childNodes);
        documentFragment.append(rootElement);

        return serializeDocumentFragmentIntoString(documentFragment);
      }),
    [containerClassName, externalLinkAlt, renderMarkdown, styleOptions, transformHTMLContent]
  );
}
